import { useEffect, useState } from "react";
import { clientsAPI } from "@/services/api";
import {
  Client,
  ClientCreateRequest,
  ClientUpdateRequest,
} from "@/types/clients";
import { Button } from "@/components/ui/shadcn/button";
import { Input } from "@/components/ui/shadcn/input";
import { Table } from "@/components/ui/shadcn/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/shadcn/dialog";
import { Card } from "@/components/ui/shadcn/card";
import { DialogDescription } from "@radix-ui/react-dialog";
import { Header } from "@/components/layout/Header";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash, CheckIcon, UserPlus, Database } from "lucide-react";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";

export default function ClientsDashboard() {
  const { toast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [newClient, setNewClient] = useState<ClientCreateRequest>({
    name: "",
    email: "",
    phone: "",
    fees: 65,
    is_enabled: true,
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await clientsAPI.getClients(true);
      setClients(response);

      // Optional: Log successful data fetch in dev mode
      if (import.meta.env.DEV) {
        console.log("Clients fetched successfully:", response.length);
      }
    } catch (error) {
      handleApiError("Fetch Clients", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.email) {
      toast({
        title: "Validation Error",
        description: "Name and Email are required!",
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const createdClient = await clientsAPI.createClient(newClient);
      showSuccessToast(toast, "Success", "Client created successfully!");
      setDialogOpen(false);
      fetchClients();
      resetForm();

      // Log successful creation in dev mode
      if (import.meta.env.DEV) {
        console.log("Client created successfully:", createdClient);
      }
    } catch (error) {
      handleApiError("Create Client", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClient = async () => {
    if (!selectedClient) return;

    setLoading(true);
    try {
      const updateData: ClientUpdateRequest = {
        name: selectedClient.name,
        email: selectedClient.email,
        phone: selectedClient.phone ?? undefined,
        fees: selectedClient.fees,
        is_enabled: selectedClient.is_enabled,
      };
      const updatedClient = await clientsAPI.updateClient(
        selectedClient.id,
        updateData
      );
      showSuccessToast(toast, "Success", "Client updated successfully!");
      setDialogOpen(false);
      fetchClients();
      resetForm();

      // Log successful update in dev mode
      if (import.meta.env.DEV) {
        console.log("Client updated successfully:", updatedClient);
      }
    } catch (error) {
      handleApiError("Update Client", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;

    setLoading(true);
    try {
      await clientsAPI.deleteClient(id);
      showSuccessToast(toast, "Success", "Client deleted successfully!");
      fetchClients();

      // Log successful deletion in dev mode
      if (import.meta.env.DEV) {
        console.log("Client deleted successfully, ID:", id);
      }
    } catch (error) {
      handleApiError("Delete Client", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handlePurgeTestData = async () => {
    if (!window.confirm("Are you sure you want to purge all test data? This action cannot be undone.")) return;

    setLoading(true);
    try {
      await clientsAPI.purgeTestData();
      showSuccessToast(toast, "Success", "Test data purged successfully!");
      fetchClients();

      // Log successful purge in dev mode
      if (import.meta.env.DEV) {
        console.log("Test data purged successfully");
      }
    } catch (error) {
      handleApiError("Purge Test Data", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewClient({ name: "", email: "", phone: "", fees: 65, is_enabled: true });
    setSelectedClient(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-6">
        <h1 className="text-2xl font-bold text-gray-800">Clients Management</h1>
        <Card className="p-6 shadow-lg rounded-xl">
          <div className="flex justify-between mb-4">
            <Button
              className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center"
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
            >
              <UserPlus className="mr-2 h-5 w-5" /> Add Client
            </Button>
          </div>
          {loading ? (
            <p className="text-gray-500 text-center">Loading clients...</p>
          ) : (
            <Table className="w-full border rounded-md">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Phone</th>
                  <th className="p-2 text-left">Fees</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id} className="border-t hover:bg-gray-50">
                    <td className="p-2">{client.name}</td>
                    <td className="p-2">{client.email}</td>
                    <td className="p-2">{client.phone || "N/A"}</td>
                    <td className="p-2">{client.fees}</td>
                    <td className="p-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          client.is_enabled
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {client.is_enabled ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-2 flex gap-2">
                      <Button
                        variant="outline"
                        className="flex items-center"
                        onClick={() => {
                          setSelectedClient(client);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex items-center"
                        onClick={() => handleDeleteClient(client.id)}
                      >
                        <Trash className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                      {client.email === "test@test.com" && (
                        <Button
                          variant="outline"
                          className="flex items-center bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
                          onClick={handlePurgeTestData}
                        >
                          <Database className="h-4 w-4 mr-1" />
                          Purge Data
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {/* Client Dialog (Create & Edit) */}
        {isDialogOpen && (
          <Dialog open={isDialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="sm:max-w-[450px] rounded-lg shadow-2xl">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedClient ? "Edit Client" : "Create Client"}
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  {selectedClient
                    ? "Update the client details below."
                    : "Fill in the client details below to create a new client."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <Input
                    className="w-full"
                    placeholder="Enter client name"
                    value={selectedClient ? selectedClient.name : newClient.name}
                    onChange={(e) =>
                      selectedClient
                        ? setSelectedClient({
                            ...selectedClient,
                            name: e.target.value,
                          })
                        : setNewClient({ ...newClient, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <Input
                    className="w-full"
                    placeholder="Enter email address"
                    value={
                      selectedClient ? selectedClient.email : newClient.email
                    }
                    onChange={(e) =>
                      selectedClient
                        ? setSelectedClient({
                            ...selectedClient,
                            email: e.target.value,
                          })
                        : setNewClient({ ...newClient, email: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  <Input
                    className="w-full"
                    placeholder="Enter phone number"
                    value={
                      selectedClient
                        ? selectedClient.phone ?? ""
                        : newClient.phone ?? ""
                    }
                    onChange={(e) =>
                      selectedClient
                        ? setSelectedClient({
                            ...selectedClient,
                            phone: e.target.value,
                          })
                        : setNewClient({ ...newClient, phone: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Fees (basis points)</label>
                  <Input
                    className="w-full"
                    placeholder="Enter fee in basis points (1 BPS = 0.01%)"
                    type="number"
                    min="0"
                    max="10000"
                    value={selectedClient ? selectedClient.fees : newClient.fees}
                    onChange={(e) =>
                      selectedClient
                        ? setSelectedClient({
                            ...selectedClient,
                            fees: Number(e.target.value),
                          })
                        : setNewClient({
                            ...newClient,
                            fees: Number(e.target.value),
                          })
                    }
                  />
                </div>
                <div className="flex items-center space-x-2 pt-2">
                  <input
                    type="checkbox"
                    id="is_enabled"
                    checked={selectedClient ? selectedClient.is_enabled : newClient.is_enabled}
                    onChange={(e) =>
                      selectedClient
                        ? setSelectedClient({
                            ...selectedClient,
                            is_enabled: e.target.checked,
                          })
                        : setNewClient({
                            ...newClient,
                            is_enabled: e.target.checked,
                          })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="is_enabled"
                    className="text-sm font-medium text-gray-700"
                  >
                    Active
                  </label>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={
                      selectedClient ? handleUpdateClient : handleCreateClient
                    }
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-800 hover:to-blue-900"
                  >
                    <CheckIcon className="h-5 w-5 mr-2" />
                    {loading
                      ? selectedClient
                        ? "Updating..."
                        : "Creating..."
                      : selectedClient
                      ? "Update"
                      : "Create"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
}
